import { useMemo } from 'react';
import { Zap, Clock, X, CheckCircle, DollarSign } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { ehDoSegmento, rotuloSegmento, useSegmentoTriagem } from '@/components/kanban/triagem/segmentoTriagem';
import { TriagemShell } from '@/components/kanban/triagem/TriagemShell';
import { TRIAGEM_TH } from '@/components/kanban/triagem/triagemTabela';
import { useLinhasOcupadas } from '@/components/kanban/triagem/acaoTriagem';
import { agruparErroSolarPorPedido, useTriagemErroSolar } from './useTriagemErroSolar';
import { TriagemErroSolarRow } from './TriagemErroSolarRow';
import { separarAparelho, useFlagsFaturamento } from '@/components/kanban/triagem/flagsFaturamento';

interface TriagemErroSolarModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function TriagemErroSolarModal({ isOpen, onClose }: TriagemErroSolarModalProps) {
  const {
    itens: itensTodos,
    isLoading,
    refetch,
    moverParaFinal,
    realizarInput,
    moverParaTTRecursos,
    isMovingToFinal,
    isRealizandoInput,
    isMovingToTTRecursos,
  } = useTriagemErroSolar();
  /* Só o pedido clicado fica ocupado; o resto da fila continua livre
     (`triagem/acaoTriagem.ts`). */
  const { ocupada, ocupar } = useLinhasOcupadas();
  const naLinha = (pedidoId: string, fn: () => Promise<unknown>) => { void ocupar(pedidoId, fn).catch(() => undefined); };

  /* ⛔ CNPJ e CPF NÃO SE MISTURAM NA FILA (Douglas 17/09/2026). A régua de
     entrada desta triagem é a MESMA de sempre — o que muda é a fila mostrar um
     segmento por vez, e os contadores do topo falarem do que está na tela.
     A separação é filtro, nunca coluna nova: nada troca de fila fora da
     triagem. Lei em `triagem/segmentoTriagem.ts`. */
  const { segmento, setSegmento } = useSegmentoTriagem();
  const itens = useMemo(
    () => itensTodos.filter((r) => ehDoSegmento(r.tipo_pessoa, segmento)),
    [itensTodos, segmento],
  );

  const pedidos = useMemo(() => agruparErroSolarPorPedido(itens), [itens]);
  const uniquePedidos = pedidos.length;
  // Aparelho anda SEPARADO do faturamento — tem KPI próprio e não é venda de
  // linha. Régua única em `flagsFaturamento.ts`.
  const flagsFat = useFlagsFaturamento();
  const { faturamento: totalValue, qtdAparelhos } = separarAparelho(
    itens, flagsFat, (i) => i.produto, (i) => (i.subtotal || 0),
  );

  return (
    <TriagemShell
      isOpen={isOpen}
      onFechar={onClose}
      Icone={Zap}
      titulo="Triagem Erro Solar"
      subtitulo="fila do quadro Tramitação Vivo · prazo 2 h úteis"
      contadores={{ pedidos: uniquePedidos, itens: itens.length, valor: totalValue, aparelhos: qtdAparelhos }}
      segmento={segmento}
      onSegmento={setSegmento}
      consulta={{ chave: 'erro_solar', pedidoIds: itens.map(i => i.pedido_id) }}
      sla={null}
      fifo={true}
      legenda={[
        { key: 'manter', Icone: Clock, label: 'Manter' },
        { key: 'input-realizado', Icone: CheckCircle, label: 'tira da fila' },
        { key: 'tt-recursos', Icone: DollarSign, label: 'TT Recursos (dívida)' },
        { key: 'pedido-cancelado', Icone: X, label: 'cancela o pedido' },
      ]}
      isLoading={isLoading}
      onAtualizar={async () => { await refetch(); toast({ title: 'Dados atualizados', duration: 1500 }); }}
      vazio={
        itens.length === 0
          ? { titulo: 'Nenhum pedido pendente', descricao: 'Todos os erros solares foram processados' }
          : undefined
      }
    >
            <table className="w-full" style={{ tableLayout: 'fixed' }}>
              <thead className="sticky top-0 z-10 bg-surface">
                <tr>
                  <th className={`${TRIAGEM_TH} w-[40px]`}>Fila</th>
                  <th className={TRIAGEM_TH}>{rotuloSegmento(segmento)}</th>
                  <th className={TRIAGEM_TH}>Razão Social</th>
                  <th className={`${TRIAGEM_TH} w-[96px]`}>Cotação</th>
                  <th className={`${TRIAGEM_TH} w-[96px]`}>Pedido</th>
                  <th className={TRIAGEM_TH}>Produto</th>
                  <th className={`${TRIAGEM_TH} w-[44px]`}>Qtd</th>
                  <th className={`${TRIAGEM_TH} w-[92px]`}>Valor</th>
                  <th className={`${TRIAGEM_TH} w-[128px]`}>Tempo</th>
                  <th className={`${TRIAGEM_TH} w-[240px]`}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {pedidos.map((pedido, posicao) => {
                  const oc = ocupada(pedido.pedido_id);
                  return (
                  <TriagemErroSolarRow
                    key={pedido.pedido_id}
                    pedido={pedido}
                    isTopo={posicao === 0}
                    posicao={posicao}
                    onMoverParaFinal={(itemId) => naLinha(pedido.pedido_id, () => moverParaFinal(itemId))}
                    onRealizarInput={(itemId, numeroCotacao) => naLinha(pedido.pedido_id, () => realizarInput({ itemId, numeroCotacao }))}
                    onMoverParaTTRecursos={(itemId) => naLinha(pedido.pedido_id, () => moverParaTTRecursos(itemId))}
                    ocupada={oc}
                    isMovingToFinal={oc && isMovingToFinal}
                    isRealizandoInput={oc && isRealizandoInput}
                    isMovingToTTRecursos={oc && isMovingToTTRecursos}
                  />
                  );
                })}
              </tbody>
            </table>
    </TriagemShell>
  );
}
